<template>
  <div class="flex flex-col">
    <div
      v-for="(item, index) in data?.cards"
      :key="item?.title || index"
      :class="[item.rowStyle !== 'dotted-lines' && 'mb-5 last:mb-0 lg:mb-24']"
    >
      <div
        class="flex flex-col lg:items-center"
        :class="[
          reverse(index) ? 'lg:flex-row-reverse' : 'lg:flex-row',
          item.rowStyle !== 'dotted-lines' && 'mb-5 last:mb-0 lg:mb-24',
        ]"
      >
        <div
          v-if="item?.mediaType === 'image'"
          class="h-full px-4 lg:basis-[50%] lg:px-0 xl:basis-[60%] 2xl:basis-[38%]"
        >
          <ParallaxBgWrapper :field_parallax_effect="item.field_parallax_effect">
            <Image
              v-if="item?.image?.url && item?.image?.type === 'image'"
              :url="item.image.url"
              :alt="item.image.alt"
              :class="[
                'aspect-square w-full shrink-0 overflow-hidden rounded-2xl md:aspect-[3/2.5]',
                reverse(index)
                  ? 'lg:rounded-br-none lg:rounded-tr-none'
                  : 'lg:rounded-bl-none lg:rounded-tl-none',
              ]"
              width="800"
              height="600"
              sizes="xs:100vw sm:400 lg:600 xl:800"
              fit="cover"
            />
          </ParallaxBgWrapper>
        </div>

        <div
          v-if="(item?.mediaType === 'video' && item?.video?.url) || item?.video?.file"
          class="shrink-0 lg:basis-[60%] lg:px-4"
        >
          <component
            :is="item?.video?.url ? VideoIframe : VideoPlayer"
            v-if="item?.video?.url || item?.video?.file"
            :url="item.video.url || item?.video?.file"
            class="aspect-video w-full self-start lg:rounded-2xl"
          />
        </div>

        <div class="flex shrink flex-col justify-center px-4 pt-6 lg:px-36 lg:py-5 xl:py-12">
          <div class="lg:max-w-2xl">
            <SubTitle class="text-blue">
              {{ item.subtitle }}
            </SubTitle>
            <Title
              :custom-style="twMerge(['lg:text-4xl 2xl:text-5xl', useDarkMode ? 'text-white' : ''])"
              :level="2"
            >
              {{ item.title }}
            </Title>
            <div
              :class="
                twMerge(
                  'custom-list prose max-w-none lg:prose-lg prose-a:text-blue prose-a:no-underline mt-3.5 font-sans text-base leading-snug lg:text-lg lg:leading-normal',
                  useDarkMode
                    ? 'prose-p:text-white prose-li:text-white prose-ul:text-white prose-headings:text-white hover:prose-a:text-blue-hover text-white'
                    : 'hover:prose-a:text-blue-dark text-black',
                )
              "
              v-html="item.copy"
            ></div>

            <CopyBox
              v-if="item.copyCode?.code"
              :code="item.copyCode?.code"
              :label="item.copyCode?.label"
              successMessage="Rabattkod kopierad!"
              class="mt-4 max-w-[415px]"
            />

            <Link
              v-if="item?.link?.url"
              :title="item.link.title"
              :link="item.link.url"
              :external="item.link?.type === 'ext'"
              class="!mt-10 w-full lg:w-auto"
              :aria-label="item.title"
            />
          </div>
        </div>
      </div>
      <div
        v-if="item.rowStyle === 'dotted-lines'"
        class="flex w-full justify-center py-16 lg:py-36"
      >
        <Dots :reverse="reverse(index)" class="hidden lg:block" />
        <DotsMobile :reverse="reverse(index)" class="lg:hidden" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// Sibling: packages/astro-ui/src/components/DynamicBlocks/ImageTextBlock.astro
import SubTitle from '../atoms/SubTitle.vue'
import Title from '../atoms/Title.vue'
import Link from '../atoms/Link.vue'
import Image from '../atoms/Image.vue'
import CopyBox from '../atoms/CopyBox.vue'
import VideoIframe from '../atoms/VideoIframe.vue'
import VideoPlayer from '../atoms/VideoPlayer.vue'
import Dots from '../atoms/Dots.vue'
import DotsMobile from '../atoms/DotsMobile.vue'
import type { ICardData } from '../../types'
import ParallaxBgWrapper from '../atoms/ParallaxBgWrapper.vue'
import { computed, inject, ref, type Ref } from 'vue'
import { twMerge } from 'tailwind-merge'

interface IImageTextCard extends ICardData {
  mediaType: 'image' | 'video'
  field_parallax_effect?: boolean
  rowStyle?: string
  video?: {
    url?: string
    file?: string
  }
  copyCode?: {
    label?: string
    code?: string
  }
}

export interface IImageTextBlock {
  id: string
  cards?: IImageTextCard[]
}

interface Props {
  data: IImageTextBlock
}

defineProps<Props>()

const reverse = (index: number) => index % 2 !== 0
const settings = inject<Ref<string[]>>('settings', ref([]))
const useDarkMode = computed(() => settings.value?.includes('dark_theme') ?? false)
</script>
